import { useState, type ReactNode } from 'react';
import { Box, IconButton, Paper, Tooltip } from '@mui/material';
import DragIndicatorOutlined from '@mui/icons-material/DragIndicatorOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';

export interface ReorderableListProps<T> {
  /** The ordered items to render. The list reflects this order verbatim. */
  items: T[];
  /** Stable id for a row (drag identity + React key). */
  getItemId: (item: T) => string;
  /**
   * Fired with the full **reordered array** after a drag-drop or keyboard move.
   * The parent persists the new order (e.g. a server-authoritative reorder POST)
   * and feeds the fresh order back through `items`.
   */
  onReorder: (reordered: T[]) => void;
  /** Renders a row's content (the handle/chrome is supplied by this component). */
  renderItem: (item: T, index: number) => ReactNode;
  /**
   * Marks an item as pinned — it cannot be dragged and always stays after the
   * movable items (e.g. an `Any` catch-all rule that must evaluate last).
   */
  isFixed?: (item: T) => boolean;
  /** Tooltip shown on a fixed item's (disabled) handle (translated copy). */
  fixedHint?: string;
  /** Accessible label for a movable row's drag handle (translated copy). */
  handleLabel?: string;
  /**
   * Optional per-row handle label; overrides `handleLabel` for that row. Use it
   * where each row has a distinct identity (e.g. "Reorder rule priority 3").
   */
  getItemLabel?: (item: T, index: number) => string;
  disabled?: boolean;
  /** Accessible label for the list. */
  ariaLabel?: string;
}

/**
 * Generic drag-to-reorder list. Rows carry a grab handle; drag-and-drop and
 * keyboard (ArrowUp/ArrowDown on a focused handle) both reorder. `isFixed` items
 * are pinned to the end and never move. Dependency-free (native HTML5 DnD) and
 * token-tied via MUI — the row content is entirely caller-supplied through
 * `renderItem`, keeping the primitive domain-agnostic.
 */
export function ReorderableList<T>({
  items,
  getItemId,
  onReorder,
  renderItem,
  isFixed,
  fixedHint,
  handleLabel = 'Reorder',
  getItemLabel,
  disabled = false,
  ariaLabel,
}: ReorderableListProps<T>) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const fixedOf = (item: T) => (isFixed ? isFixed(item) : false);

  const move = (fromId: string, toId: string) => {
    if (fromId === toId) return;
    const movable = items.filter((i) => !fixedOf(i));
    const fixed = items.filter((i) => fixedOf(i));
    const from = movable.findIndex((i) => getItemId(i) === fromId);
    const to = movable.findIndex((i) => getItemId(i) === toId);
    if (from < 0 || to < 0) return;
    const next = movable.slice();
    const [moved] = next.splice(from, 1);
    if (!moved) return;
    next.splice(to, 0, moved);
    onReorder([...next, ...fixed]);
  };

  const moveBy = (id: string, delta: number) => {
    const movable = items.filter((i) => !fixedOf(i));
    const idx = movable.findIndex((i) => getItemId(i) === id);
    const target = idx + delta;
    if (idx < 0 || target < 0 || target >= movable.length) return;
    const targetItem = movable[target];
    if (!targetItem) return;
    move(id, getItemId(targetItem));
  };

  return (
    <Box
      role="list"
      aria-label={ariaLabel}
      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
    >
      {items.map((item, index) => {
        const id = getItemId(item);
        const fixed = fixedOf(item);
        const draggable = !disabled && !fixed;
        return (
          <Paper
            key={id}
            role="listitem"
            variant="outlined"
            draggable={draggable}
            onDragStart={
              draggable
                ? (e) => {
                    setDragId(id);
                    // jsdom's synthetic drag events carry no dataTransfer; guard so the
                    // reorder tests (and any non-DnD environment) don't throw.
                    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
                  }
                : undefined
            }
            onDragOver={
              draggable
                ? (e) => {
                    e.preventDefault();
                    if (overId !== id) setOverId(id);
                  }
                : undefined
            }
            onDrop={
              draggable
                ? (e) => {
                    e.preventDefault();
                    if (dragId) move(dragId, id);
                    setDragId(null);
                    setOverId(null);
                  }
                : undefined
            }
            onDragEnd={() => {
              setDragId(null);
              setOverId(null);
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: '10px 14px',
              borderRadius: '8px',
              opacity: dragId === id ? 0.5 : 1,
              borderColor: overId === id && dragId !== id ? 'primary.main' : 'divider',
              transition: 'border-color 120ms',
            }}
          >
            {fixed ? (
              <Tooltip title={fixedHint ?? ''}>
                <Box
                  component="span"
                  sx={{
                    display: 'grid',
                    placeItems: 'center',
                    width: 34,
                    height: 34,
                    color: 'text.disabled',
                    flexShrink: 0,
                  }}
                >
                  <LockOutlined sx={{ fontSize: 18 }} />
                </Box>
              </Tooltip>
            ) : (
              <IconButton
                size="small"
                aria-label={getItemLabel ? getItemLabel(item, index) : handleLabel}
                disabled={disabled}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    moveBy(id, -1);
                  } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    moveBy(id, 1);
                  }
                }}
                sx={{ cursor: 'grab', flexShrink: 0, color: 'text.secondary' }}
              >
                <DragIndicatorOutlined sx={{ fontSize: 18 }} />
              </IconButton>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>{renderItem(item, index)}</Box>
          </Paper>
        );
      })}
    </Box>
  );
}
